import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { useLocale } from '../context';
import { setHelpCmsBundle } from '../data/helpCmsRuntime';
import { normalizeLogo, readCachedLogo, writeCachedLogo } from '../utils/logoConfig';

const SiteContentContext = createContext(null);
const CONTENT_CACHE_PREFIX = 'vb_cms_content_v2:';
const CONTENT_POLL_MS = 60000;

function isPromoModalOpen() {
  try {
    return Boolean(new URLSearchParams(window.location.search).get('promoId'));
  } catch {
    return false;
  }
}

const CONTENT_FETCH_MS = 12000;
/** Persist across tabs/reloads so header + slider paint immediately on return visits */
const CONTENT_STORAGE_KEYS = ['localStorage', 'sessionStorage'];

const EMPTY_CONFIRM_CLOSE = {
  enabled: true,
  image: '/uploads/confirm-close-banner.webp',
  title: '',
  titleEn: '',
  lead: '',
  leadEn: '',
  highlight: '',
  highlightEn: '',
  offer: '',
  offerEn: '',
  desc: '',
  descEn: '',
  body: '',
  bodyEn: '',
  cta: '',
  ctaEn: '',
};

const EMPTY_REGISTER = {
  mainDesktop: null,
  offer: null,
  offerRu: null,
  offerSlides: null,
  offerSlidesRu: null,
  offerSlideSeconds: '5',
  mainMobile: null,
  mainMobileRu: null,
  background: null,
  bgColor: '#d4004b',
  gradientTo: '#2d0010',
  text: null,
  confirmClose: EMPTY_CONFIRM_CLOSE,
};

const EMPTY_SEO = {
  title: '',
  titleEn: '',
  description: '',
  descriptionEn: '',
  ogImage: '',
  favicon: '',
};

const EMPTY_NOTIFICATION_BANNER = {
  enabled: false,
  text: '',
  texts: [],
  textTr: '',
  textEn: '',
  textsTr: [],
  textsEn: [],
  buttonText: 'Detaylar',
  linkUrl: '/promo-vip',
};

const EMPTY_GUEST_POPUP = {
  title: '',
  subtitle: '',
  cta: '',
  imageUrl: '',
  delaySeconds: 1,
  displaySeconds: 0,
  cooldownHours: 0,
  active: false,
};

const EMPTY_ERROR_PAGE = {
  title: '',
  titleEn: '',
  message: '',
  messageEn: '',
  button: '',
  buttonEn: '',
  buttonBg: '#d42a28',
  buttonColor: '#ffffff',
};

const EMPTY_ERROR_PAGES = {
  oauth: { ...EMPTY_ERROR_PAGE },
  route: { ...EMPTY_ERROR_PAGE },
  generic: { ...EMPTY_ERROR_PAGE },
  notFound: { ...EMPTY_ERROR_PAGE },
};

const LOADING_VALUE = {
  loaded: false,
  loading: true,
  refreshing: false,
  homeBannerSlides: [],
  casinoSlides: [],
  showcaseCards: [],
  casinoMenuTabs: [],
  guestWelcomePopup: EMPTY_GUEST_POPUP,
  notificationBanner: EMPTY_NOTIFICATION_BANNER,
  scenario: null,
  nav: null,
  sponsors: [],
  settings: null,
  logo: null,
  seo: EMPTY_SEO,
  register: EMPTY_REGISTER,
  errorPages: EMPTY_ERROR_PAGES,
  promoActions: [],
  promoTournaments: [],
  menuBanners: [],
  modals: {},
  pages: {},
  getPromoByPromoId: () => null,
};

function cmsNavToClient(nav) {
  if (!nav) return null;
  const hasAny = nav.header_main?.length
    || nav.header_ctrl?.length
    || nav.mobile_bottom?.length
    || nav.footer_left?.length
    || nav.footer_right?.length;
  return hasAny ? nav : null;
}

function buildContentValue(content) {
  const slides = content?.slides || {};
  const register = content?.register || EMPTY_REGISTER;
  const errorPages = content?.errorPages || EMPTY_ERROR_PAGES;
  const cmsNav = cmsNavToClient(content?.nav);
  const promoActions = content?.promos?.actions || [];
  const promoTournaments = content?.promos?.tournaments || [];
  const menuBanners = content?.menuBanners || [];
  const modals = content?.modals || {};
  const pages = content?.pages || {};
  const help = content?.help || null;

  const getPromoByPromoId = (promoId) => {
    if (!promoId) return null;
    const all = [...promoActions, ...promoTournaments];
    return all.find((p) => p.promoId === promoId || p.id === promoId) || null;
  };

  return {
    loaded: true,
    loading: false,
    homeBannerSlides: slides.home_banner || [],
    casinoSlides: slides.casino_slider || [],
    showcaseCards: content?.showcase || [],
    casinoMenuTabs: content?.casinoMenuTabs || [],
    guestWelcomePopup: content?.guestPopup || EMPTY_GUEST_POPUP,
    notificationBanner: content?.notificationBanner || EMPTY_NOTIFICATION_BANNER,
    scenario: content?.scenario || null,
    nav: cmsNav,
    sponsors: content?.sponsors || [],
    settings: content?.settings || null,
    logo: normalizeLogo(content?.logo),
    seo: content?.seo || EMPTY_SEO,
    register: {
      mainDesktop: register.mainDesktop || null,
      offer: register.offer || null,
      offerRu: register.offerRu || null,
      offerSlides: register.offerSlides || null,
      offerSlidesRu: register.offerSlidesRu || null,
      offerSlideSeconds: register.offerSlideSeconds || '5',
      mainMobile: register.mainMobile || null,
      mainMobileRu: register.mainMobileRu || null,
      background: register.background || null,
      bgColor: register.bgColor || '#d4004b',
      gradientTo: register.gradientTo || '#2d0010',
      text: register.text || null,
      confirmClose: {
        ...EMPTY_CONFIRM_CLOSE,
        ...(register.confirmClose || {}),
      },
    },
    errorPages: {
      oauth: { ...EMPTY_ERROR_PAGE, ...(errorPages.oauth || {}) },
      route: { ...EMPTY_ERROR_PAGE, ...(errorPages.route || {}) },
      generic: { ...EMPTY_ERROR_PAGE, ...(errorPages.generic || {}) },
      notFound: { ...EMPTY_ERROR_PAGE, ...(errorPages.notFound || {}) },
    },
    promoActions,
    promoTournaments,
    menuBanners,
    modals,
    pages,
    help,
    getPromoByPromoId,
  };
}

function readStoredContent(locale) {
  const key = `${CONTENT_CACHE_PREFIX}${locale}`;
  for (const storeName of CONTENT_STORAGE_KEYS) {
    try {
      const store = window[storeName];
      if (!store) continue;
      const raw = store.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      /* try next store */
    }
  }
  return null;
}

function writeStoredContent(locale, data) {
  if (!data) return;
  const key = `${CONTENT_CACHE_PREFIX}${locale}`;
  const raw = JSON.stringify(data);
  for (const storeName of CONTENT_STORAGE_KEYS) {
    try {
      window[storeName]?.setItem(key, raw);
    } catch {
      /* ignore quota / private mode */
    }
  }
}

function fetchWithTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error('content timeout')), ms);
    }),
  ]);
}

function readInitialCache() {
  if (typeof window === 'undefined') return {};
  const out = {};
  for (const locale of ['tr', 'en']) {
    const hit = readStoredContent(locale);
    if (hit) out[locale] = hit;
  }
  return out;
}

function readInitialLoaded(cache) {
  const out = {};
  Object.keys(cache).forEach((locale) => {
    out[locale] = true;
  });
  return out;
}

export function SiteContentProvider({ children }) {
  const [cache, setCache] = useState(() => readInitialCache());
  const [loadedMap, setLoadedMap] = useState(() => {
    const initial = readInitialCache();
    return readInitialLoaded(initial);
  });
  const [refreshing, setRefreshing] = useState(false);
  const { locale } = useLocale();
  const localeRef = useRef(locale);
  const cacheRef = useRef(cache);
  const loadedMapRef = useRef(loadedMap);

  localeRef.current = locale;
  cacheRef.current = cache;
  loadedMapRef.current = loadedMap;

  const applyContent = useCallback((loc, data) => {
    setCache((prev) => ({ ...prev, [loc]: data }));
    setLoadedMap((prev) => ({ ...prev, [loc]: true }));
    if (data) writeStoredContent(loc, data);
    if (data?.logo) writeCachedLogo(data.logo);
    if (loc === localeRef.current) {
      setHelpCmsBundle(data?.help || null);
    }
  }, []);

  const fetchContent = useCallback(async (loc, { silent = false } = {}) => {
    const hasCache = Boolean(cacheRef.current[loc]);
    if (silent && hasCache) setRefreshing(true);

    try {
      const data = await fetchWithTimeout(api.content(loc), CONTENT_FETCH_MS);
      if (data) applyContent(loc, data);
      else if (!hasCache) applyContent(loc, null);
    } catch {
      if (!hasCache) applyContent(loc, null);
    } finally {
      if (silent && hasCache) setRefreshing(false);
    }
  }, [applyContent]);

  useEffect(() => {
    const hasCache = Boolean(cacheRef.current[locale]);
    fetchContent(locale, { silent: hasCache });
  }, [locale, fetchContent]);

  useEffect(() => {
    const id = setInterval(() => {
      if (isPromoModalOpen()) return;
      if (loadedMapRef.current[localeRef.current]) {
        fetchContent(localeRef.current, { silent: true });
      }
    }, CONTENT_POLL_MS);

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (isPromoModalOpen()) return;
      if (loadedMapRef.current[localeRef.current]) {
        fetchContent(localeRef.current, { silent: true });
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchContent]);

  const promoLookupRef = useRef((promoId) => null);
  promoLookupRef.current = (promoId) => {
    if (!promoId) return null;
    const content = cacheRef.current[localeRef.current];
    const actions = content?.promos?.actions || [];
    const tournaments = content?.promos?.tournaments || [];
    const all = [...actions, ...tournaments];
    return all.find((p) => p.promoId === promoId || p.id === promoId) || null;
  };
  const getPromoByPromoIdStable = useCallback((promoId) => promoLookupRef.current(promoId), []);

  const value = useMemo(() => {
    const loading = !loadedMap[locale];
    if (loading) {
      return {
        ...LOADING_VALUE,
        refreshing,
        logo: readCachedLogo(),
        getPromoByPromoId: getPromoByPromoIdStable,
      };
    }

    const built = {
      ...buildContentValue(cache[locale] ?? null),
      refreshing,
      getPromoByPromoId: getPromoByPromoIdStable,
    };
    setHelpCmsBundle(cache[locale]?.help || null);
    return built;
  }, [cache, loadedMap, locale, refreshing, getPromoByPromoIdStable]);

  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  const ctx = useContext(SiteContentContext);
  if (ctx) return ctx;
  return LOADING_VALUE;
}
